// Resume content for /resume, transcribed verbatim from Maurice's resume doc (Oct 2026).
// The downloadable PDF at public/resume/ is generated from the /resume page, so edit here
// and regenerate the PDF to keep both in sync.

export const resume = {
  name: 'Maurice Sanders',
  roles: ['Technical Product Lead', 'Prompt Engineer', 'Context Engineer', 'Automation Specialist'],
  contact: {
    location: 'Clermont, FL',
    email: 'maurice@mauricethefirst.com',
    phone: '305-434-0738',
    github: { label: 'github.com/MrAfroTech', href: 'https://github.com/MrAfroTech' },
  },
  summary: [
    "You don't need another person who can write code. You need someone who can walk into a problem nobody owns, find what's actually broken, and ship the fix. That's what I do.",
    'I came up through DevOps at Comcast and QA at Bellomy, where my job was finding what breaks before your customers do. Then I founded Seamlessly and learned the harder lesson: software only matters if it moves a number the business cares about.',
    "Today I run AI the way a CEO runs a team, and every model has one job. ChatGPT is my business analyst: it turns quantitative problems into precise build instructions for Cursor. Claude is my project manager: it scopes websites and front-end work and tells Cursor exactly what to build. I make the calls and approve what ships. Giving each model a single role keeps every prompt tight, and it cut my token usage and AI subscription costs by 50%. That's how I built a 0DTE options trading platform that runs in production, a ticketing platform that earns a fee on every ticket sold, and an AI concierge that plans a guest's entire night out.",
    "Hand me the problem. I'll come back with it solved, and with the results to prove it.",
  ],
  builds: [
    {
      title: 'Automated Trading & Market Intelligence Platform',
      status: 'In production',
      bullets: [
        'Built a production 0DTE options platform that finds, risk-checks, executes and reports trades across SPY, QQQ and IWM.',
        'Designed three systematic strategies: Opening Range Breakout, Premarket Breakout, and EMA/VWAP, each with close-through and hold confirmation.',
        'Engineered 11 automated risk controls, including daily loss limits, ticker loss locks, spread-quality filters, position limits and automated time stops.',
        'Integrated Tastytrade for live execution and Tradier and DXLink for market data, with broker position reconciliation.',
        'Separated trading state from reporting: a React dashboard shows daily and cumulative P&L, strategy performance and a calendar view without touching execution.',
      ],
      stack: 'React, Vite, Node.js, PostgreSQL / Supabase, Railway, Vercel.',
    },
    {
      title: 'Strike After Dark — Event Ticketing Platform (client)',
      status: 'Live',
      bullets: [
        'Built a full ticketing business for a nightlife brand: tiered ticket sales, Stripe Checkout, and QR entry at the door.',
        'Monetized the platform with Stripe Connect destination charges, earning an application fee on every ticket sold while paying out the venue.',
        'Made fulfillment reliable with a webhook plus success-page fallback, idempotent purchase writes through a Supabase RPC, and isolated staging tables.',
        'Automated confirmation emails with QR tickets through Klaviyo events; protected checkout with Cloudflare Turnstile and QR checksum validation.',
      ],
      stack: 'React 18, Vite, Vercel serverless (Node.js), Stripe, Supabase / PostgreSQL, Klaviyo.',
    },
    {
      title: 'Zoey — AI Digital Concierge for Concier, powered by Eventbrella',
      status: 'Private testing',
      bullets: [
        "Led product and architecture for an AI concierge that learns each guest's preferences and plans a complete night out: dinner, drinks and events.",
        'Directed AI coding agents across discovery, backend, frontend, security and deployment; reviewed and approved every change before production.',
        'Cut onboarding from 8 screens to 5; measured median API speedups of 23–32% after diagnosing a cross-region function/database mismatch.',
        'Secured guest data with row-level security, private schemas and server-to-server token forwarding; 57 automated backend tests passing.',
      ],
      stack: 'Node.js, server-rendered HTML, Supabase (Postgres, Auth, RLS), Vercel.',
    },
  ],
  experience: [
    {
      role: 'Founder & Fractional CTO',
      company: 'Seamlessly',
      location: 'Remote',
      dates: 'Jan 2025 – Present',
      intro:
        'Founded and led technical development of an AI-powered B2B SaaS platform for restaurants and hospitality businesses.',
      bullets: [
        'Architected the full-stack platform on React, Node.js, Supabase (PostgreSQL) and AWS Lambda, with secure auth, Stripe subscriptions and real-time data sync.',
        'Integrated Claude, OpenAI and Gemini with custom prompting strategies for platform-specific content, cutting content creation time by 80%.',
        'Built a scheduling system that manages 500+ posts a month across platforms and client accounts.',
        'Built an AI image pipeline (FLUX, DALL-E) that produced 1,000+ on-brand images for client campaigns.',
        'Secured a partnership with Kia Center (Orlando Magic arena) for social media automation services.',
        'Led a team of 3 developers and 2 content specialists with weekly sprint planning and retrospectives.',
        'Delivered 15+ pitches to investors, partners and clients, shaping product vision and closing partnerships.',
        'Used AI-assisted development (Cursor, GitHub Copilot) to accelerate feature delivery by 50%, shipping complex UI and API integrations in fewer iterations.',
      ],
    },
    {
      role: 'Quality Assurance Engineer',
      company: 'Bellomy',
      location: 'Remote',
      dates: 'Feb 2023 – Jan 2025',
      bullets: [
        'Designed and maintained 100+ automated tests with Selenium, Java and Playwright, raising test coverage by 35%.',
        'Used AI prompting (OpenAI, Claude) to speed up test automation and cut manual debugging by 40%.',
        'Worked with development, support and product teams to cut issue resolution time by 20%.',
        'Caught critical security and functionality bugs before they reached production.',
      ],
    },
    {
      role: 'DevOps Engineer',
      company: 'Comcast',
      location: 'Remote',
      dates: 'Aug 2022 – Nov 2022',
      bullets: [
        'Designed secure, scalable AWS Step Functions workflows for serverless apps using AWS SAM.',
        'Built IAM roles and DevSecOps practices into cloud deployment pipelines.',
        'Automated front-end validation with Playwright, reducing manual testing by 40%.',
      ],
    },
  ],
  skills: [
    {
      label: 'AI & Prompting',
      items:
        'Prompt engineering, context engineering, directing AI coding agents, Claude Code, Cursor, ChatGPT, Perplexity, Claude API, OpenAI, Gemini, Hugging Face',
    },
    {
      label: 'Product',
      items: 'Product strategy, phased roadmaps, architecture decisions, release management, live testing and triage',
    },
    { label: 'Languages', items: 'JavaScript, TypeScript, Python, Java, SQL' },
    { label: 'Frontend', items: 'React, Next.js, Vite, Tailwind CSS, server-rendered HTML, CSS design systems' },
    {
      label: 'Backend & Cloud',
      items:
        'Node.js, Express, REST APIs, Vercel serverless, Railway, AWS (Lambda, SAM, Step Functions, IAM), Docker, GitHub Actions',
    },
    { label: 'Data', items: 'PostgreSQL, Supabase (RLS, RPC, Auth), SQL migrations, idempotent writes' },
    {
      label: 'Payments & Integrations',
      items: 'Stripe Checkout, Connect and Tax, webhooks, Klaviyo, Cloudflare Turnstile',
    },
    {
      label: 'Markets',
      items:
        'Tastytrade and Tradier APIs, DXLink market data, options mechanics, risk-engine design, broker reconciliation',
    },
    { label: 'Test Automation', items: 'Playwright, Selenium, Cucumber, TestNG, JUnit' },
    { label: 'Security', items: 'Secure API development, DevSecOps, IAM, RBAC, system hardening' },
  ],
  education: [
    { title: 'Northern Illinois University', detail: 'B.S., Computer Science' },
    { title: 'CompTIA A+', detail: '(2025) · Network+, Security+, CySA+ (expected 2027)' },
  ],
  moreProjects: [
    {
      title: 'Blue Flame — client restaurant website',
      text: "Rebuilt a client's restaurant site on the Strike After Dark codebase.",
    },
    {
      title: 'financialMaturity — the CFO for my life',
      text: 'A personal finance system I built to run my own money like a business.',
    },
    {
      title: 'QuantResearch',
      href: 'https://github.com/MrAfroTech/QuantResearch',
      text: 'A hub for quantitative strategies combining data science, algorithmic trading and market analysis; the groundwork for the trading platform above.',
    },
    {
      title: 'ezDrinkBot — AI agent with secure chatbot',
      text: 'Full-stack app on Node.js, React and Supabase with secure auth, LLM-based interaction, and testing across staging and production.',
    },
    {
      title: 'Automated Visual Regression Testing',
      text: 'Playwright and Pixelmatch framework that enforces pixel-level UI accuracy and flags unintended changes to critical components.',
    },
  ],
};
